import { ItemsList } from "../../../common/components/itemsList/ItemsList";
import { DefaultButton } from "../../../common/components/button/defaultButton/DefaultButton";
import { IconType } from "../../../common/icons/Icon";
import { RenderWhen } from "../../../common/infrastructure/components/RenderWhen";
import { GiftVoucherStatus } from "../../../common/enums";
import { useGiftVouchersPage } from "./hooks/useGiftVouchersPage";
import { GiftVoucherListItem } from "./components/GiftVoucherListItem";
import { GiftVoucherFormDialog } from "./components/GiftVoucherFormDialog";
import { GiftVoucherDetailsDialog } from "./components/GiftVoucherDetailsDialog";
import { GiftVoucherRedeemDialog } from "./components/GiftVoucherRedeemDialog";
import { formatVoucherAmount } from "./utils/giftVoucherUtils";
import styles from "./GiftVouchersPage.module.scss";

export const GiftVouchersPage = () => {
  const {
    isMobile,
    vouchers,
    isLoading,
    outstandingLiability,
    unsoldCount,
    isFormDialogVisible,
    isRedeemDialogVisible,
    editedVoucher,
    selectedVoucherId,
    openCreateDialog,
    openEditDialog,
    closeFormDialog,
    openRedeemDialog,
    closeRedeemDialog,
    openDetails,
    closeDetails,
    toggleDeletion,
  } = useGiftVouchersPage();

  const pageActions = (
    <div className={isMobile ? styles.root_actions_mobile : styles.root_actions}>
      <DefaultButton
        label="Създай нов"
        width="auto"
        onClick={openCreateDialog}
      />
      <DefaultButton
        label="Осребри ваучер"
        design="outline"
        width="auto"
        onClick={openRedeemDialog}
      />
    </div>
  );

  return (
    <>
      <ItemsList
        isLoading={isLoading}
        className={styles.root}
        title="Подаръчни ваучери"
        tooltip={
          "Продаденият ваучер е задължение, не приход. Парите влизат в касата веднага, " +
          "а приходът се признава чак когато ваучерът бъде усвоен. Ваучерът важи само в " +
          "обекта, който го е продал."
        }
        noItemsText="Все още няма създадени ваучери."
        items={vouchers ?? []}
        showItemsCount
        enableSearch
        fullHeight
        itemOnClick={(id) => !!id && openDetails(id)}
        deletedFilter={{
          storageKey: "giftVouchers",
          isDeleted: (voucher) => voucher.isDeleted,
          sectionTitle: "Изтрити ваучери",
          toggleLabel: "Покажи изтритите ваучери",
        }}
        actionButton={isMobile ? undefined : pageActions}
        headerContent={isMobile ? pageActions : undefined}
        footer={
          <RenderWhen condition={!isLoading && (vouchers?.length ?? 0) > 0}>
            <div className={styles.root_summary}>
              <span className={styles.root_summary_label}>
                Непродадени ваучери:
              </span>
              <span className={styles.root_summary_value}>{unsoldCount}бр</span>
              <span className={styles.root_summary_label}>
                Дължима сума по ваучери:
              </span>
              <span className={styles.root_summary_value}>
                {formatVoucherAmount(outstandingLiability)}
              </span>
            </div>
          </RenderWhen>
        }
        swipeLeftActions={[
          {
            type: "onClick",
            iconType: IconType.DeleteIcon,
            // Deleting a sold voucher would erase a liability the studio was paid for
            // (32007); the selling order is what gets deleted instead.
            disabled: (voucher) => voucher.status !== GiftVoucherStatus.New,
            enablePrompt: true,
            promptMessage:
              "Сигурни ли сте, че искате да изтриете този ваучер? Кодът му остава запазен и няма да бъде издаден повторно.",
            onClick: toggleDeletion,
          },
        ]}
        swipeRightActions={[
          {
            type: "onClick",
            iconType: IconType.EditIcon,
            // Once sold, the face value is a booked liability and the code is printed
            // and in someone's hands, so the API refuses the edit (32006).
            disabled: (voucher) => voucher.status !== GiftVoucherStatus.New,
            onClick: openEditDialog,
          },
        ]}
        itemTemplate={(voucher, index, actionButtons, className) => (
          <GiftVoucherListItem
            key={voucher?.id ?? index}
            className={className}
            voucher={voucher}
            actionButtons={actionButtons}
          />
        )}
      />

      <GiftVoucherFormDialog
        voucher={editedVoucher}
        visible={isFormDialogVisible}
        onHide={closeFormDialog}
      />

      <GiftVoucherRedeemDialog
        visible={isRedeemDialogVisible}
        onHide={closeRedeemDialog}
      />

      <GiftVoucherDetailsDialog
        voucherId={selectedVoucherId}
        visible={!!selectedVoucherId}
        onHide={closeDetails}
      />
    </>
  );
};
